// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/acquirers/HlsAcquirer.ts
// v4.9  — original
// v4.10 — disableSource / enableSource
// v4.11 — loop pour flux HLS simples (non-Xtream)
// v4.12 — autoRetry : reload manifest in-place sans recréer le MediaStream
// v4.13 — restauration du gain audio après rebindAudioTrack
// v4.14 — isRetrying : verrou anti-concurrence pour autoRetry
// v4.15 — Suppression TTL proactif, _resolveManifestUrl (HEAD, MediaMTX 302+cookie),
//          withCredentials commun, isRenewing fusionné dans isRetrying,
//          _renewXtream immédiat sur erreur fatale.
//          HEAD dans _resolveManifestUrl pour ne pas consommer le manifest.
// v4.18 — resolveBaseUrl : HEAD sans Range pour ne pas consommer de token CDN,
//          lecture X-Upstream-Status pour diagnostic.
//          Logs de traçage structurés dans acquire(), _waitCanPlay, _attachHls
//          (préfixe [ACQ], [WCP], [ATH]) pour faciliter le diagnostic sans
//          polluer la prod — conditionnés à HlsAcquirer.debug = true.
//          Correction finally/_renewXtream : logique succès/échec clarifiée,
//          isRetrying libéré correctement dans les deux chemins.
// v4.18 — Renouvellement préventif Xtream : le watchdog compte les erreurs réseau
//          non-fatales consécutives sur les segments. À partir de 3 erreurs, il
//          déclenche _renewXtream pendant que le buffer absorbe encore — la coupure
//          devient imperceptible au lieu d'attendre l'erreur fatale.
//          Buffer hls.js augmenté : maxBufferLength=60s, maxMaxBufferLength=120s,
//          maxBufferSize=120MB pour maximiser la fenêtre de renouvellement invisible.
// v4.19 — Deux silences qui perdaient un flux pour de bon, cf. le retour de régie
//          du 03/09/2026 :
//          1. la période de grâce « ignorait » les erreurs fatales par un simple
//             return, alors qu'une erreur fatale arrête le chargement de hls.js
//             jusqu'à ce qu'on le relance. Elle REPREND désormais (startLoad sur
//             networkError, recoverMediaError sur mediaError), avec un plafond
//             au-delà duquel le traitement fatal ordinaire tranche ;
//          2. `await video.play()` dans acquire() n'était borné par rien : une
//             promesse jamais tranchée suspendait l'acquisition sans aucun log.
//             Bornée par PLAY_SETTLE_MS — sans avaler les vrais rejets.
//          Les messages neufs portent le préfixe v4.19 : c'est ce qui permet de
//          lire dans un log distant si le correctif est réellement en ligne, ou
//          si le navigateur rejoue un ancien build.
// ============================================================================

import { WWW_URLS } from '../../../../backendServices/urls';
import { Source, SourceAcquirer, HlsSourceConfig } from '../types';
import { SourceRegistry } from '../SourceRegistry';
import { resolveLiveStreamUrl } from '../../../../backendServices/liveStreamManagment/api_xtreamCode';
import { AudioSourceRegistry } from '../../audioPipeline/AudioSourceRegistry';
import { noteHlsBytes, forgetHlsSource } from '../../../../backendServices/liveStreamManagment/api_bandwidth';
import { captureMediaElement, capturesViaWebAudio, stopMediaElementCapture } from './mediaElementCapture';
import { registerProbeGauge, startResourceProbe } from '../../resourceProbe';
import { DIAGNOSTIC_PROBES_ENABLED } from '../../../../backendServices/debugManagment/remoteLogger';

// 🔍 DIAGNOSTIC 2026-10-23 (cf. resourceProbe.ts) : instances hls.js et gardes de gel
// encore en vie dans toute la page, tous HlsAcquirer confondus.
const hlsInstances = new Set<WeakRef<any>>();
const activeFreezeGuards = new Map<unknown, string>();

registerProbeGauge('hls.js', () => {
  let alive = 0, attached = 0;
  for (const ref of Array.from(hlsInstances)) {
    const h = ref.deref();
    if (!h) { hlsInstances.delete(ref); continue; }
    // hls.js remet url et media à null dans destroy().
    if (h.url != null || h.media != null) alive++;
    if (h.media != null) attached++;
  }
  return `${alive} vivantes/${attached} attachées`;
});
registerProbeGauge('gardes de gel', () => {
  const ids = Array.from(activeFreezeGuards.values());
  return `${ids.length} [${ids.join(', ')}]`;
});

// ── Constantes ────────────────────────────────────────────────────────────────

type UrlProtocol = 'hls' | 'dash' | 'native' | 'unsupported';

const PROXY_PREFIX   = `${WWW_URLS.broadcastingServer}/http-proxy?url=`;

// ── Cookies : nos hôtes, et eux seuls ─────────────────────────────────────────
// MediaMTX (cast.kleonpro.tv) répond au manifest par une redirection assortie
// d'un cookie de session : sans `withCredentials`, la requête suivante repart
// sans lui et le flux ne démarre jamais. Sur un flux tiers, c'est exactement
// l'inverse. La règle CORS interdit le joker `Access-Control-Allow-Origin: *`
// dès qu'une requête porte des identifiants — or c'est ce que répond la quasi
// totalité des CDN publics. Le navigateur rejette alors la réponse sans la lire,
// hls.js n'en retire aucun code HTTP et signale un `manifestLoadError httpCode=0` :
// un flux parfaitement joignable qui paraît injoignable. On n'envoie donc de
// cookies qu'à nos propres hôtes.
const CREDENTIALED_HOSTS = new Set([new URL(PROXY_PREFIX).hostname]);

function sendsCredentials(url: string): boolean {
  try {
    const { hostname } = new URL(url, window.location.href);
    return hostname === window.location.hostname || CREDENTIALED_HOSTS.has(hostname);
  } catch {
    return false;
  }
}

// Un flux Xtream porte ses identifiants dans le chemin (/live/USER/PASS/ID.m3u8)
// et n'a jamais rien à faire d'un cookie. L'exclure vaut mieux que le laisser au
// jugé : servi en direct depuis un de nos hôtes, il hériterait sinon du régime
// « credentials » réservé à MediaMTX, et le moindre `Access-Control-Allow-Origin: *`
// du vhost Xtream suffirait à faire rejeter le manifest par le navigateur.
function needsCookies(config: HlsSourceConfig, url: string): boolean {
  return !config.xtream && sendsCredentials(url);
}

const REATTACH_BASE_MS    =  2_000;
const REATTACH_MAX_MS     = 30_000;
const WATCHDOG_GRACE_MS   =  8_000; // traitement allégé des erreurs fatales X ms après un attach réussi

// ── Période de grâce : ignorer n'est pas rattraper ─────────────────────────────
//
// LE DÉFAUT (mesuré le 03/09/2026, chaîne Lovelight TV).
// La grâce introduite en v4.18 répondait à un vrai problème — hls.js émet des
// erreurs transitoires dans les secondes qui suivent MANIFEST_PARSED, et les
// traiter comme définitives déclenchait des renouvellements pour rien. Mais elle
// se contentait d'un `return` : l'erreur était écrite dans la console, et rien
// d'autre n'était fait.
//
// Or une erreur FATALE arrête le chargement de hls.js. Définitivement. La
// bibliothèque attend qu'on la relance — `startLoad()` sur une erreur réseau,
// `recoverMediaError()` sur une erreur média — et personne ne le faisait.
// L'erreur n'était donc pas « ignorée » : elle était mortelle, en silence.
//
// Constaté ce matin-là à 06:36:03. Le retour de régie venait d'attacher le muxer
// HLS de la chaîne, créé 15 s plus tôt : le segment annoncé par le manifeste
// n'était pas encore écrit, d'où un fragLoadError fatal 287 ms après l'attach —
// exactement le genre d'incident transitoire que la grâce vise. Résultat : le
// lecteur n'a plus jamais demandé un seul octet. Côté MediaMTX, ses sessions se
// sont refermées « closed: inactive » et plus aucune requête n'est arrivée ;
// côté régie, le tuyau est resté déclaré mais à zéro débit, sans erreur, sans
// nouvelle tentative — un retour qui tourne dans le vide jusqu'à ce que
// l'opérateur coupe et rallume la chaîne.
//
// LA REPRISE. Pendant la grâce, on relance le chargement au lieu de rendre la
// main. Le délai laisse le temps à ce qui manquait d'arriver (typiquement le
// premier segment d'un muxer qui vient de naître) ; le plafond garantit qu'un
// flux réellement mort finit par tomber dans le traitement fatal ordinaire —
// autoRetry ou _renewXtream — plutôt que de boucler ici.
const GRACE_RESUME_MS     =  1_000;
const GRACE_MAX_RESUMES   =      3;

// Délai au bout duquel on cesse d'attendre un verdict de video.play() dans
// acquire() — cf. le commentaire à cet endroit. Aligné sur la course déjà
// utilisée par _renewXtream sur son élément provisoire.
const PLAY_SETTLE_MS      =  3_000;

// ── Lecture figée : la détecter, la rattraper, la dire ─────────────────────────
//
// La vignette de la Playlist dans le multiview se figeait de temps en temps alors
// que le rendu final — qui vient du serveur, pas de ce lecteur — continuait. Rien
// ne surveillait ce lecteur : hls.js a ses propres reprises, mais un `currentTime`
// qui ne bouge plus sans erreur levée (trou à une jonction de la playlist, lecteur
// décroché du bord du direct, <video> mis en pause par le navigateur) restait figé
// indéfiniment, et SANS la moindre trace dans les logs.
//
// Ce garde regarde le temps de lecture une fois par seconde. Immobile depuis
// FREEZE_STAGE_MS alors que le lecteur est censé tourner, il intervient par paliers
// de plus en plus fermes (retour au bord du direct → récupération média → reprise
// du chargement) et journalise chaque palier avec l'état du lecteur : si le gel
// revient, le log dit enfin ce qui s'est passé. Il ne fait rien d'un onglet masqué
// (le navigateur y ralentit tout) ni d'un lecteur volontairement en pause.
const FREEZE_CHECK_MS = 1_000;
const FREEZE_STAGE_MS = 3_000;

// Durée de lecture démutée avant une seconde tentative de capture — cf.
// _captureWithAudio. N'est utilisée QUE si la capture éclair n'a ramené aucune
// piste audio : pendant ce laps de temps, le son sort réellement aux haut-parleurs.
const CAPTURE_UNMUTE_FALLBACK_MS = 80;

// ── Cache de latence CDN ──────────────────────────────────────────────────────
// Mémorise le temps de réponse observé de resolveBaseUrl par host CDN.
// Utilisé pour ajuster dynamiquement le seuil du renouvellement préventif :
// si le CDN répond en 3s, on ne déclenche pas un renouvellement sur des timeouts
// de 10s qui sont normaux pour lui.
// Clé : hostname CDN (ex: "185.245.0.129"), valeur : moyenne glissante en ms.
const cdnLatencyCache = new Map<string, number>();
const CDN_LATENCY_ALPHA = 0.3; // facteur lissage exponentiel (EMA)

function recordCdnLatency(host: string, ms: number): void {
  const prev = cdnLatencyCache.get(host);
  const ema  = prev === undefined ? ms : prev * (1 - CDN_LATENCY_ALPHA) + ms * CDN_LATENCY_ALPHA;
  cdnLatencyCache.set(host, Math.round(ema));
}

function getCdnLatency(host: string): number {
  return cdnLatencyCache.get(host) ?? 0;
}

// Retourne un timeout adapté au CDN : max(timeoutMin, latence × facteur)

const HLS_BASE_CONFIG: Record<string, any> = {
  manifestLoadingTimeOut  : 10_000,
  manifestLoadingMaxRetry : 2,
  levelLoadingTimeOut     : 10_000,
  levelLoadingMaxRetry    : 2,
  fragLoadingTimeOut      : 10_000,
  fragLoadingMaxRetry     : 2,
  // v4.18 — buffer agressif : réduit les coupures perçues lors du renouvellement CDN.
  // hls.js bufferise maxBufferLength secondes d'avance — pendant le renouvellement
  // Xtream (~2-5s), la lecture continue sur le buffer existant sans interruption visible.
  maxBufferLength         : 60,           // secondes de buffer cible (augmenté de 30→60)
  maxMaxBufferLength      : 120,          // plafond absolu (augmenté de 60→120)
  maxBufferSize           : 120_000_000,  // 120 MB max (proportionnel)
  maxBufferHole           : 0.5,          // tolérance trous dans le buffer
  highBufferWatchdogPeriod: 2,            // vérification buffer toutes les 2s
  backBufferLength        : 10,           // conserve 10s déjà lues — permet à la video de
                                          // rejouer sans coupure si le thread est momentanément bloqué
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function detectProtocol(url: string): UrlProtocol {
  const u = url.toLowerCase();
  if (u.includes('.m3u8') || u.startsWith('hls+'))  return 'hls';
  if (u.includes('.mpd')  || u.startsWith('dash+')) return 'dash';
  if (/^(rtsp|rtsps|rtmp|rtmps|srt):\/\//.test(u)) return 'unsupported';
  return 'native';
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const s = document.createElement('script');
    s.src     = src;
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error(`Impossible de charger ${src}`));
    document.head.appendChild(s);
  });
}

// v4.18 — HEAD sans Range header.
// Raisons :
//   1. GET Range consommait parfois un token CDN one-shot côté upstream.
//   2. HEAD suffit : X-Final-Url est posé par nginx dans tous les cas
//      (location /http-proxy et @follow_redirect).
//   3. X-Upstream-Status permet de distinguer 200/206/403/404 pour diagnostic.

async function resolveBaseUrl(proxyUrl: string, retries = 3): Promise<string | null> {
  for (let i = 0; i < retries; i++) {
    try {
      // GET sans Range (v4.18) : HEAD n'est pas dans la liste nginx Allow-Methods
      // du proxy (GET + OPTIONS uniquement). On fait un GET normal mais on ignore
      // le body — on ne lit que les headers X-Final-Url et X-Upstream-Status.
      // La v4.14 utilisait Range:bytes=0-0 ce qui pouvait consommer un token CDN.
      const t0 = Date.now();
      const response = await fetch(proxyUrl, {
        method : 'GET',
        signal : AbortSignal.timeout(8_000),
      });

      const elapsed = Date.now() - t0;
      const upstreamStatus = response.headers.get('X-Upstream-Status') ?? '?';
      const finalUrl       = response.headers.get('X-Final-Url');

      console.log(
        `[HlsAcquirer v4.18] resolveBaseUrl tentative ${i + 1}/${retries}` +
        ` — HTTP ${response.status}, upstream=${upstreamStatus}` +
        ` finalUrl=${finalUrl ?? '(absent)'} (${elapsed}ms)`
      );

      if (!finalUrl) {
        console.warn(`[HlsAcquirer v4.18] resolveBaseUrl : X-Final-Url absent (upstream ${upstreamStatus})`);
        if (i < retries - 1) await new Promise(r => setTimeout(r, 1_000));
        continue;
      }

      // 403/404 upstream = token CDN déjà invalide ou flux inexistant
      if (upstreamStatus === '403' || upstreamStatus === '404') {
        console.warn(`[HlsAcquirer v4.18] resolveBaseUrl : upstream ${upstreamStatus} — baseUrl récupérée mais flux peut être invalide`);
      }

      const baseUrl = finalUrl.substring(0, finalUrl.lastIndexOf('/') + 1);

      // Enregistrer la latence CDN pour le renouvellement adaptatif
      try {
        const cdnHost = new URL(finalUrl).hostname;
        recordCdnLatency(cdnHost, elapsed);
        console.log(`[HlsAcquirer v4.18] latence CDN ${cdnHost} : ${elapsed}ms → EMA=${getCdnLatency(cdnHost)}ms`);
      } catch {}

      console.log(`[HlsAcquirer v4.18] baseUrl résolue :`, baseUrl);
      return baseUrl;

    } catch (err) {
      console.warn(`[HlsAcquirer v4.18] resolveBaseUrl tentative ${i + 1}/${retries} échouée :`, err);
      if (i < retries - 1) await new Promise(r => setTimeout(r, 1_000));
    }
  }
  console.error('[HlsAcquirer v4.18] Impossible de résoudre X-Final-Url après', retries, 'tentatives');
  return null;
}

function buildProxyLoaderConfig(HlsLib: any, baseUrl: string): Record<string, any> {
  const origin = new URL(baseUrl).origin;

  function rewriteUrl(url: string): string {
    // URL déjà proxifiée — ne pas re-wrapper
    if (url.startsWith(PROXY_PREFIX)) return url;
    // HTTPS → accessible directement par le navigateur, pas besoin du proxy
    if (/^https:\/\//i.test(url)) return url;
    // HTTP → doit passer par le proxy (mixed-content / CORS)
    if (/^http:\/\//i.test(url))  return `${PROXY_PREFIX}${url}`;
    if (url.startsWith('/'))      return `${PROXY_PREFIX}${origin + url}`;
    return `${PROXY_PREFIX}${baseUrl + url}`;
  }

  const DefaultLoader = HlsLib.DefaultConfig?.loader ?? HlsLib.XhrLoader;
  if (!DefaultLoader) {
    console.error('[HlsAcquirer v4.18] Loader introuvable dans hls.js');
    return {};
  }

  class ProxyLoader extends DefaultLoader {
    constructor(config: any) { super(config); }
    load(context: any, config: any, callbacks: any): void {
      const original: string = context.url;
      context.url = rewriteUrl(original);
      console.debug(`[ProxyLoader] requête : ${original} → ${context.url}`);

      const originalOnSuccess = callbacks.onSuccess;
      callbacks.onSuccess = (response: any, stats: any, ctx: any, networkDetails: any) => {
        const data = response.data;

        if (
          typeof data === 'string'
          && data.trimStart().startsWith('<')
          && !data.includes('#EXTM3U')
        ) {
          console.error(
            `[ProxyLoader] Réponse HTML reçue pour ${ctx.url}` +
            ` — erreur CDN (quota/509/403), conversion en networkError`
          );
          callbacks.onError(
            { code: 509, text: 'CDN error (HTML response instead of media)' },
            stats,
            ctx,
            networkDetails
          );
          return;
        }

        if (typeof data === 'string' && data.includes('#EXTM3U')) {
          response.data = data.replace(
            /^(\/[^\s]+)$/gm,
            (match: string) => `${PROXY_PREFIX}${origin + match}`
          );
        }

        originalOnSuccess(response, stats, ctx, networkDetails);
      };

      super.load(context, config, callbacks);
    }
  }

  return { loader: ProxyLoader };
}

// ── buildDirectLoaderConfig ───────────────────────────────────────────────────
//
// Un manifest chargé en direct — sans passer par le proxy — peut très bien
// désigner ses segments en HTTP clair : un serveur Xtream écrit ses URL absolues
// avec le schéma sur lequel il a été configuré, sans savoir par où on l'a joint.
// Depuis une page HTTPS, le navigateur refuse ces requêtes sans même les émettre
// (mixed content). hls.js n'en voit qu'un fragLoadError immédiat, sur une
// playlist qui vient pourtant de se parser sans une erreur — le manifest passe,
// le flux ne démarre jamais.
//
// Deux réécritures, et rien d'autre :
//   • même hôte que le manifest → on repasse simplement en HTTPS. On sait qu'il
//     le parle : le manifest lui-même est arrivé par là.
//   • autre hôte → le proxy, comme pour les playlists proxifiées.
// Les URL relatives sont déjà résolues par hls.js contre l'URL du manifest quand
// le loader est appelé : il n'y a rien à en faire ici.
// Doit rester synchronisé avec MONITOR_SOURCE_ID dans useMonitorStream.ts — pas
// d'import direct pour éviter un cycle (useMonitorStream importe déjà HlsAcquirer).
const MONITOR_SOURCE_ID_FOR_NO_COOKIE_MANIFEST = 'hls-monitor-retour';

function buildDirectLoaderConfig(HlsLib: any, manifestUrl: string, sourceId?: string): Record<string, any> {
  const DefaultLoader = HlsLib.DefaultConfig?.loader ?? HlsLib.XhrLoader;
  if (!DefaultLoader) {
    console.error('[HlsAcquirer v4.18] Loader introuvable dans hls.js');
    return {};
  }

  let manifestHost = '';
  try { manifestHost = new URL(manifestUrl).hostname; } catch {}

  function rewriteUrl(url: string): string {
    if (!/^http:\/\//i.test(url)) return url;
    try {
      const u = new URL(url);
      if (manifestHost && u.hostname === manifestHost) {
        u.protocol = 'https:';
        return u.toString();
      }
    } catch {}
    return `${PROXY_PREFIX}${url}`;
  }

  // ── Session HLS de MediaMTX (fmp4) — perdue par hls.js à la résolution ──────
  //
  // Depuis MediaMTX 1.18+ (hlsVariant: fmp4), le manifeste maître écrit son
  // propre `?session=<uuid>` sur l'URI de chaque sous-playlist
  // (« video1_stream.m3u8?session=… »). MediaMTX REFUSE en 401 toute
  // sous-playlist/segment demandé sans ce paramètre (ou un cookie hlsSession
  // correspondant). Constaté le 08/09/2026 : hls.js 1.5.17 perd ce paramètre
  // en résolvant l'URI relative — la requête part sans lui, malgré le
  // commentaire ci-dessus qui supposait la résolution fiable. On le récupère
  // donc nous-mêmes dans la réponse du manifeste, et on le recolle sur toute
  // requête qui en serait dépourvue.
  let capturedSession: string | null = null;

  function withSession(url: string): string {
    if (!capturedSession || /[?&]session=/.test(url)) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}session=${capturedSession}`;
  }

  class DirectLoader extends DefaultLoader {
    constructor(config: any) { super(config); }
    load(context: any, config: any, callbacks: any): void {
      const original: string = context.url;
      let rewritten = rewriteUrl(original);
      rewritten = withSession(rewritten);
      if (rewritten !== original) {
        console.debug(`[DirectLoader] URL réécrite : ${original} → ${rewritten}`);
        context.url = rewritten;
      }

      // Capture le `session=` du manifeste pour les requêtes suivantes (niveau,
      // piste audio, fragments) — sans toucher aux autres callbacks.
      const wrappedCallbacks = { ...callbacks };
      const isManifest = context.type === 'manifest';
      if (isManifest && typeof callbacks?.onSuccess === 'function') {
        wrappedCallbacks.onSuccess = (response: any, stats: any, ctx: any, networkDetails: any) => {
          const text: string = typeof response?.data === 'string' ? response.data : '';
          const match = text.match(/[?&]session=([A-Za-z0-9-]+)/);
          if (match) capturedSession = match[1];
          callbacks.onSuccess(response, stats, ctx, networkDetails);
        };
      }

      // Le manifeste (type 'manifest') part SANS cookie — UNIQUEMENT pour le
      // monitor de retour, jamais pour les autres sources (Playlist…) qui
      // s'appuient normalement sur la continuité du cookie et régressent si on
      // la leur retire (constaté le 08/09/2026, revert immédiat). Sur le
      // monitor spécifiquement : un hlsSession posé par un cycle précédent
      // (encore en vol au moment où le suivant démarre — la sonde tourne en
      // 1 s, les tentatives ratées relancent tout le pipeline) fait que
      // MediaMTX arrête d'écrire `?session=` dans le manifeste et mise tout
      // sur ce cookie, qu'une requête concurrente fait alors tourner sous le
      // pied de celle-ci. Sans AUCUN cookie envoyé, MediaMTX traite
      // systématiquement le client comme neuf et écrit `?session=`
      // explicitement — déterministe, vérifié à la main plusieurs fois.
      //
      // ⚠️ `xhrSetup` est lu par XhrLoader depuis `this.xhrSetup`, fixé UNE
      // FOIS au CONSTRUCTEUR depuis la config globale — pas depuis le
      // paramètre `config` de `load()`. hls.js crée un loader NEUF par
      // requête (« Loader can only be used once »), donc réassigner
      // `this.xhrSetup` ICI, avant `super.load()`, s'applique bien à CETTE
      // seule requête sans affecter les autres sources.
      if (isManifest && sourceId === MONITOR_SOURCE_ID_FOR_NO_COOKIE_MANIFEST) {
        (this as any).xhrSetup = (xhr: XMLHttpRequest) => { xhr.withCredentials = false; };
      }

      super.load(context, config, wrappedCallbacks);
    }
  }

  return { loader: DirectLoader };
}

function waitForVideoData(video: HTMLVideoElement, timeoutMs = 10_000): Promise<void> {
  return new Promise<void>(resolve => {
    if (video.readyState >= 2) { resolve(); return; }
    const cleanup = () => {
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('canplaythrough', onCanPlay);
    };
    const onCanPlay = () => { cleanup(); resolve(); };
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('canplaythrough', onCanPlay);
    setTimeout(() => { cleanup(); resolve(); }, timeoutMs);
  });
}

// ── Types internes ────────────────────────────────────────────────────────────

export interface HlsEntry {
  video           : HTMLVideoElement;
  hls?            : any;
  dash?           : any;
  source          : Source;
  reattachAttempts: number;
  isRetrying      : boolean;
  watchdogHandler : ((event: any, data: any) => void) | null;
  loopAbort?      : AbortController;
  // v4.18 — timestamp du dernier _attachHls réussi.
  // Le watchdog ignore les erreurs fatales pendant WATCHDOG_GRACE_MS après un attach
  // pour éviter que levelLoadTimeOut ne déclenche _renewXtream sur un stream qui vient
  // juste de démarrer (hls.js émet parfois des erreurs dans les secondes suivant MANIFEST_PARSED).
  lastAttachTs?   : number;
  // Nombre de reprises déjà tentées PENDANT la période de grâce de l'attach
  // courant — cf. GRACE_MAX_RESUMES. Remis à zéro à chaque attach réussi, en
  // même temps que lastAttachTs : les deux décrivent le même attach.
  graceResumes?   : number;
}


// ── Acquirer ──────────────────────────────────────────────────────────────────

export class HlsAcquirer implements SourceAcquirer {

  private freezeGuards = new Map<string, ReturnType<typeof setInterval>>();

  private _stopFreezeGuard(sourceId: string): void {
    const t = this.freezeGuards.get(sourceId);
    if (t) { clearInterval(t); this.freezeGuards.delete(sourceId); activeFreezeGuards.delete(t); }
  }

  private _startFreezeGuard(sourceId: string, entry: HlsEntry): void {
    this._stopFreezeGuard(sourceId);
    let lastTime = entry.video.currentTime;
    let lastMove = Date.now();
    let stage = 0;
    let frozenSince = 0;
    let pausedLogged = false;

    // 🔍 DIAGNOSTIC 2026-10-20 (cf. mémoire regie-desync-son-image-on-air) : la
    // lecture avance-t-elle au rythme du temps réel ? Une lecture plus rapide que
    // l'horloge empile du son dans le graphe audio pendant que l'image, elle, est
    // dessinée sans attendre.
    let clockBase: { wall: number; t: number } | null = null;
    let clockTick = 0;
    const timer = setInterval(() => {
      const v = entry.video;
      if (DIAGNOSTIC_PROBES_ENABLED && ++clockTick % Math.max(1, Math.round(30_000 / FREEZE_CHECK_MS)) === 0) {
        const wall = performance.now() / 1000;
        if (!clockBase || v.paused || v.seeking || v.currentTime < clockBase.t) clockBase = { wall, t: v.currentTime };
        else {
          const dw = wall - clockBase.wall, dt = v.currentTime - clockBase.t;
          console.warn(`[HLS-CLOCK] ${sourceId} : depuis ${dw.toFixed(0)} s, lecture ${dt.toFixed(2)} s`
            + ` (${((dt - dw) / dw * 60_000).toFixed(0)} ms/min) rate=${v.playbackRate} drops=${(v as any).getVideoPlaybackQuality?.().droppedVideoFrames ?? '—'}`);
        }
      }
      // Onglet masqué : le navigateur ralentit tout, rien à juger.
      if (document.hidden) {
        lastTime = v.currentTime; lastMove = Date.now(); stage = 0;
        return;
      }
      // Pause SUBIE : la seule pause voulue passe par disableSource. Chrome met un
      // <video> en pause quand l'onglet est masqué, et il restait ensuite figé sans
      // qu'aucun palier ne s'en occupe (la garde prenait la pause pour un choix).
      if (v.paused && !v.ended && v.readyState >= 1 && !this._disabledIds.has(sourceId)) {
        if (!pausedLogged) {
          pausedLogged = true;
          console.warn(`[HlsAcquirer v4.20] ${sourceId} : lecteur en PAUSE sans l'avoir demandé`
            + ` (t=${v.currentTime.toFixed(2)} readyState=${v.readyState}) — reprise`);
        }
        const edge = entry.hls?.liveSyncPosition;
        try {
          if (typeof edge === 'number' && edge > v.currentTime + 0.3) v.currentTime = edge;
          v.play().catch(() => {});
        } catch {}
        lastTime = v.currentTime; lastMove = Date.now(); stage = 0;
        return;
      }
      if (pausedLogged && !v.paused) {
        pausedLogged = false;
        console.warn(`[HlsAcquirer v4.20] ${sourceId} : lecteur sorti de pause`);
      }
      // Rien à surveiller : pause voulue, fin de lecture, rien de chargé.
      if (v.paused || v.ended || v.readyState < 1) {
        lastTime = v.currentTime; lastMove = Date.now(); stage = 0;
        return;
      }
      if (Math.abs(v.currentTime - lastTime) > 0.05) {
        if (stage > 0) {
          console.warn(`[HlsAcquirer v4.20] ${sourceId} : lecture reprise après ${Math.round((Date.now() - frozenSince) / 1000)} s (palier ${stage})`);
        }
        lastTime = v.currentTime; lastMove = Date.now(); stage = 0;
        return;
      }
      const stuck = Date.now() - lastMove;
      if (stage >= 3 || stuck < FREEZE_STAGE_MS * (stage + 1)) return;
      if (stage === 0) frozenSince = lastMove;
      stage++;

      const hls = entry.hls;
      const b = v.buffered;
      const ranges = Array.from({ length: b.length }, (_, i) => `${b.start(i).toFixed(1)}–${b.end(i).toFixed(1)}`).join(' ');
      console.warn(
        `[HlsAcquirer v4.20] ${sourceId} : lecture FIGÉE depuis ${Math.round(stuck / 1000)} s — palier ${stage}/3`
        + ` | t=${v.currentTime.toFixed(2)} readyState=${v.readyState} tampon=[${ranges}]`
        + ` | bord du direct=${hls?.liveSyncPosition != null ? Number(hls.liveSyncPosition).toFixed(1) : '—'}`
        + ` latence=${hls?.latency != null ? Number(hls.latency).toFixed(1) : '—'} niveau=${hls?.currentLevel ?? '—'}`,
      );
      try {
        if (stage === 1) {
          const edge = hls?.liveSyncPosition;
          if (typeof edge === 'number' && edge > v.currentTime + 0.3) v.currentTime = edge;
          else v.play().catch(() => {});
        } else if (stage === 2) {
          hls?.recoverMediaError?.();
          v.play().catch(() => {});
        } else {
          hls?.startLoad?.();
          v.play().catch(() => {});
        }
      } catch (e) { console.warn(`[HlsAcquirer v4.20] ${sourceId} : reprise impossible`, e); }
    }, FREEZE_CHECK_MS);
    this.freezeGuards.set(sourceId, timer);
    activeFreezeGuards.set(timer, sourceId);
  }

  // v4.18 — flag de debug : positionner à true en console pour activer les
  // traces fines [ACQ] [WCP] [ATH] sans recompiler.
  // Ex : window.__hlsAcquirerInstance.debug = true
  // Remis à true le 10/08/2026 pour diagnostiquer le bug de mini-vue playlist
  // vide en mode régie (cf. _renewXtream / stream-updated) — repasser à false
  // une fois la cause confirmée, ces traces sont bavardes.
  debug = true;

  readonly entries = new Map<string, HlsEntry>();
  private registry : SourceRegistry;
  private audioSourceRegistry: AudioSourceRegistry | null = null;

  private _disabledIds = new Set<string>();

  // Callback appelé quand l'état de buffering change sur un flux.
  // sourceId : identifiant du flux concerné, isBuffering : true = en attente de données.
  // Un debounce de 300ms évite les flashs sur les micro-pauses normales.
  onBufferingChange: ((sourceId: string, isBuffering: boolean) => void) | null = null;

  // Cleanup des listeners buffering par sourceId
  private _bufferingCleanups = new Map<string, () => void>();

  constructor(registry: SourceRegistry) {
    this.registry = registry;
    startResourceProbe();
  }

  private _log(prefix: string, ...args: any[]): void {
    if (this.debug) console.log(`[HlsAcquirer v4.18][${prefix}]`, ...args);
  }

  // Branche les listeners waiting/playing sur la <video> d'un sourceId.
  // Remplace les éventuels listeners existants (appelé à chaque _attachHls + swap).
  private _attachBufferingListeners(sourceId: string, video: HTMLVideoElement): void {
    this._detachBufferingListeners(sourceId);
    if (!this.onBufferingChange) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const DEBOUNCE_MS = 300;

    const emit = (isBuffering: boolean) => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        if (!this.onBufferingChange) return;
        // Ne pas signaler si l'entry a été supprimée entre-temps
        if (!this.entries.has(sourceId)) return;
        this.onBufferingChange(sourceId, isBuffering);
      }, DEBOUNCE_MS);
    };

    const onWaiting  = () => emit(true);
    const onPlaying  = () => emit(false);
    const onStalled  = () => emit(true);
    const onCanPlay  = () => emit(false);

    video.addEventListener('waiting',  onWaiting);
    video.addEventListener('playing',  onPlaying);
    video.addEventListener('stalled',  onStalled);
    video.addEventListener('canplay',  onCanPlay);

    this._bufferingCleanups.set(sourceId, () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      video.removeEventListener('waiting',  onWaiting);
      video.removeEventListener('playing',  onPlaying);
      video.removeEventListener('stalled',  onStalled);
      video.removeEventListener('canplay',  onCanPlay);
    });
  }

  private _detachBufferingListeners(sourceId: string): void {
    const cleanup = this._bufferingCleanups.get(sourceId);
    if (cleanup) { cleanup(); this._bufferingCleanups.delete(sourceId); }
  }

  setAudioSourceRegistry(audioReg: AudioSourceRegistry): void {
    this.audioSourceRegistry = audioReg;
    console.log('[HlsAcquirer v4.18] audioSourceRegistry injecté');
  }

  // ── disableSource ─────────────────────────────────────────────────────────

  disableSource(sourceId: string): void {
    console.log(`[HlsAcquirer v4.18] disableSource ${sourceId}`);
    this._disabledIds.add(sourceId);
    const entry = this.entries.get(sourceId);
    if (!entry) return;
    if (entry.hls) { try { entry.hls.stopLoad(); } catch {} }
    try { entry.video.pause(); } catch {}
  }

  // ── enableSource ──────────────────────────────────────────────────────────

  enableSource(sourceId: string): void {
    console.log(`[HlsAcquirer v4.18] enableSource ${sourceId}`);
    this._disabledIds.delete(sourceId);
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  async acquire(source: Source): Promise<MediaStream> {

    if (this._disabledIds.has(source.id)) {
      console.log(`[HlsAcquirer v4.18] acquire() bloqué — ${source.id} désactivé`);
      throw new Error(`[HlsAcquirer] Source ${source.id} désactivée`);
    }

    const config   = source.config as HlsSourceConfig;
    const url      = config.url;
    const protocol = detectProtocol(url);

    this._log('ACQ', source.id, `protocol=${protocol} url=${url}`);

    if (protocol === 'unsupported') {
      throw new Error(
        `Le protocole ${url.split('://')[0].toUpperCase()} ne peut pas être lu dans le navigateur.`
      );
    }

    this.releaseEntry(source.id);

    const video = Object.assign(document.createElement('video'), {
      muted: true, autoplay: true, playsInline: true, crossOrigin: 'anonymous',
    });
    video.style.display = 'none';
    document.body.appendChild(video);

    const entry: HlsEntry = {
      video,
      source,
      reattachAttempts: 0,
      isRetrying      : false,
      watchdogHandler : null,
    };
    this.entries.set(source.id, entry);

    try {
      if (protocol === 'hls') {
        // La résolution HEAD n'existe que pour récupérer le cookie MediaMTX ;
        // envoyée à un hôte tiers elle échoue en « Failed to fetch » (mêmes
        // raisons CORS que ci-dessus) et ne rapporte rien.
        const resolvedUrl = url.startsWith(PROXY_PREFIX) || !needsCookies(config, url)
          ? url
          : await this._resolveManifestUrl(url);

        this._log('ACQ', source.id, `resolvedUrl=${resolvedUrl}`);

        await this._attachHls(source.id, resolvedUrl, entry);

        this._log('ACQ', source.id, `_attachHls OK, readyState=${video.readyState}`);

        if (config.loop && !config.xtream) {
          const ac = new AbortController();
          entry.loopAbort = ac;
          video.addEventListener('ended', () => {
            if (this._disabledIds.has(source.id)) return;
            if (!this.entries.has(source.id))     return;
            console.log(`[HlsAcquirer v4.18] ended — loop restart ${source.id}`);
            entry.hls?.stopLoad();
            entry.hls?.loadSource(resolvedUrl);
            entry.hls?.startLoad(-1);
            video.play().catch(() => {});
          }, { signal: ac.signal });
        }

      } else if (protocol === 'dash') {
        let dashjs = (window as any).dashjs;
        if (!dashjs) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/dashjs/4.7.4/dash.all.min.js');
          dashjs = (window as any).dashjs;
        }
        entry.dash = dashjs.MediaPlayer().create();
        entry.dash.initialize(video, url, true);
      } else {
        video.src = url;
      }

      this._log('ACQ', source.id, `avant play(), readyState=${video.readyState} paused=${video.paused}`);
      // La promesse de play() ne se tranche QUE quand la lecture démarre pour de
      // bon : sur un élément dont le flux s'est arrêté d'arriver, elle ne se
      // tranche jamais — ni tenue, ni rompue. `await` seul suspendait alors
      // acquire() indéfiniment, donc borrow(), donc le lecteur appelant, et sans
      // écrire une seule ligne : ni succès, ni échec, ni retry. C'est la moitié
      // silencieuse du retour perdu le 03/09/2026 (l'autre étant la période de
      // grâce, cf. GRACE_RESUME_MS).
      //
      // Un play() qui tarde n'est pas une erreur, et la vraie porte est juste en
      // dessous : _waitCanPlay tranche dans les deux sens sous 15 s et libère
      // l'entry en cas d'échec. On lui laisse donc la décision.
      //
      // Le garde ne neutralise QUE l'absence de verdict. Un play() qui rompt
      // vraiment — l'AbortError « video-only background media was paused to save
      // power », observé le 02/09/2026 — doit continuer à faire échouer
      // l'acquisition et à déclencher une nouvelle tentative : la course rejette
      // alors, et acquire() la propage comme avant. Avaler ce rejet ferait
      // capturer un élément en pause, donc une image figée sans un mot.
      let playSansVerdict = false;
      await Promise.race([
        video.play(),
        new Promise<void>(resolve =>
          setTimeout(() => { playSansVerdict = true; resolve(); }, PLAY_SETTLE_MS)
        ),
      ]);
      if (playSansVerdict) {
        console.warn(
          `[HlsAcquirer v4.19] play() toujours sans verdict après ${PLAY_SETTLE_MS}ms` +
          ` pour ${source.id} (readyState=${video.readyState}) — on laisse _waitCanPlay décider`
        );
      }
      this._log('ACQ', source.id, `play() OK, readyState=${video.readyState}`);

      await this._waitCanPlay(source.id, video);
      this._log('ACQ', source.id, `canPlay OK, readyState=${video.readyState} videoWidth=${video.videoWidth} videoHeight=${video.videoHeight}`);

      // Stabilisation AVANT la capture — l'élément reste muet pendant ce temps.
      // (Auparavant ce délai était passé démuté : 80 ms de son à l'antenne, très
      // au-delà en pratique car le thread principal est saturé par le montage de
      // la fiche chaîne. C'était le « burst » entendu à l'ouverture d'une chaîne.)
      await new Promise(r => setTimeout(r, 80));

      const stream = await this._captureWithAudio(source.id, video);

      if (!stream) {
        this.releaseEntry(source.id);
        throw new Error('captureStream() non supporté');
      }

      const vTracks = stream.getVideoTracks().length;
      const aTracks = stream.getAudioTracks().length;
      console.log(`[HlsAcquirer v4.18] ${source.id} — captureStream : ${vTracks} video, ${aTracks} audio`);
      this._attachBufferingListeners(source.id, video);

      this._log('ACQ', source.id, `tracks détail :`,
        stream.getTracks().map((t: MediaStreamTrack) =>
          `${t.kind}:${t.readyState}:enabled=${t.enabled}:muted=${t.muted}:label=${t.label}`
        )
      );

      return stream;

    } catch (err) {
      console.error(`[HlsAcquirer v4.18] acquire() échoué pour ${source.id} :`, err);
      // Ne pas releaseEntry si l'erreur vient d'un play() interrompu par _renewXtream
      // (AbortError "play() request was interrupted by a new load request").
      // Dans ce cas l'entry est déjà prise en charge par le watchdog.
      const isPlayAbort = err instanceof Error && err.name === 'AbortError';
      if (!isPlayAbort) {
        this.releaseEntry(source.id);
      }
      throw err;
    }
  }

  // ── release ────────────────────────────────────────────────────────────────

  release(source: Source): void {
    this.releaseEntry(source.id);
  }

  // ── _captureWithAudio ──────────────────────────────────────────────────────
  //
  // Capture d'un <video> avec une piste audio vivante.
  //
  // Chromium n'inclut de piste audio exploitable dans captureStream() que si
  // l'élément n'est PAS muet au moment précis de la capture. En revanche l'audio
  // déjà capturé survit à la remise en sourdine juste après — c'est ce dont dépend
  // tout le retour d'antenne, qui fonctionne alors que l'élément reste muet.
  //
  // Le point sensible : pendant la fenêtre démutée, le son du flux sort réellement
  // aux haut-parleurs. L'implémentation précédente démutait PUIS attendait (80 ms
  // de stabilisation ici, jusqu'à 5 s d'attente de `canplay` dans le chemin
  // autoRetry, le tout allongé par la charge du thread principal au montage) :
  // c'est l'origine du « burst » de son à l'ouverture d'une chaîne. On réduit donc
  // la fenêtre à l'appel synchrone de captureStream().
  //
  // Repli : si cette capture éclair ne ramène aucune piste audio — navigateur qui
  // exigerait une lecture démutée effective — on refait une capture selon
  // l'ancienne recette, pour ne pas troquer un défaut cosmétique contre une perte
  // de son. Le passage par ce repli est journalisé en warning.
  private async _captureWithAudio(
    sourceId: string,
    video   : HTMLVideoElement,
  ): Promise<MediaStream | null> {

    const capture = (): MediaStream | null => captureMediaElement(video);

    // Safari (pas de captureStream sur <video>) : capture par canvas + Web Audio.
    // L'élément doit y rester NON muet — muet, il enverrait du silence dans le
    // graphe — sans que rien ne sorte aux haut-parleurs, puisque la source audio
    // est tirée de l'élément. Ni fenêtre démutée à surveiller, ni second essai.
    if (capturesViaWebAudio(video)) {
      video.muted = false;
      return capture();
    }

    const wasMuted = video.muted;

    video.muted = false;
    const stream = capture();
    video.muted = wasMuted;

    if (!stream || stream.getAudioTracks().length > 0) return stream;

    console.warn(
      `[HlsAcquirer v4.18] ${sourceId} — capture sans piste audio,` +
      ` second essai avec ${CAPTURE_UNMUTE_FALLBACK_MS}ms de lecture démutée`
    );
    // On ne stoppe pas les pistes du premier stream : selon les navigateurs elles
    // peuvent partager la source de l'élément, et les couper compromettrait la
    // seconde capture. Elles seront collectées avec le stream abandonné.
    video.muted = false;
    await new Promise(r => setTimeout(r, CAPTURE_UNMUTE_FALLBACK_MS));
    const retried = capture();
    video.muted = wasMuted;
    return retried;
  }

  // ── _retryDelay ────────────────────────────────────────────────────────────

  private _retryDelay(entry: HlsEntry): number {
    return Math.min(REATTACH_BASE_MS * Math.pow(2, entry.reattachAttempts), REATTACH_MAX_MS);
  }

  // ── _resolveManifestUrl ────────────────────────────────────────────────────
  // v4.15/v4.18 — HEAD pour poser le cookie sans consommer le manifest.

  private async _resolveManifestUrl(url: string): Promise<string> {
    try {
      const res = await fetch(url, {
        method     : 'HEAD',
        redirect   : 'follow',
        credentials: sendsCredentials(url) ? 'include' : 'omit',
        signal     : AbortSignal.timeout(8_000),
      });
      const finalUrl = res.url || url;
      if (finalUrl !== url) {
        console.log(`[HlsAcquirer v4.18] manifest résolu : ${url} → ${finalUrl}`);
      }
      return finalUrl;
    } catch (err) {
      console.warn('[HlsAcquirer v4.18] _resolveManifestUrl échoué, URL brute conservée :', err);
      return url;
    }
  }

  // ── _removeWatchdog ────────────────────────────────────────────────────────

  private _removeWatchdog(entry: HlsEntry, HlsLib: any): void {
    if (entry.hls && entry.watchdogHandler) {
      try { entry.hls.off(HlsLib.Events.ERROR, entry.watchdogHandler); } catch {}
      entry.watchdogHandler = null;
    }
  }

  // ── _destroyHls ────────────────────────────────────────────────────────────

  private _destroyHls(entry: HlsEntry, HlsLib: any): void {
    if (!entry.hls) return;
    this._removeWatchdog(entry, HlsLib);
    try { entry.hls.destroy(); } catch {}
    entry.hls = undefined;
  }

  // ── _buildAndRegisterWatchdog ──────────────────────────────────────────────

  private _buildAndRegisterWatchdog(sourceId: string, entry: HlsEntry, HlsLib: any): void {
    let mediaErrorAttempts    = 0;
    const MAX_MEDIA_ERRORS    = 3;
    let consecutiveNetErrors  = 0;

    const watchdog = (_: any, data: any) => {
      const source = entry.source;
      const config = source.config as HlsSourceConfig;

      // ── Erreurs non-fatales : renouvellement préventif adaptatif ───────────
      // On ne déclenche pas sur des timeouts qui sont dans la plage normale de
      // latence du CDN. Le seuil = max(5 erreurs, latence CDN connue × 4 / levelLoadingTimeOut).
      if (!data.fatal) {
        if (
          config.xtream
          && data.type === 'networkError'
          && !entry.isRetrying
          && !this._disabledIds.has(sourceId)
        ) {
          // Calculer le seuil adaptatif : combien d'erreurs avant que ce soit anormal ?
          // Si le CDN met 3s à répondre et que levelLoadingTimeOut = 10s,
          // une erreur non-fatale est normale. On attend NON_FATAL_THRESHOLD_MIN
          // erreurs au minimum, ou plus si la latence CDN est connue.
          const NON_FATAL_THRESHOLD_MIN = 5;
          const cdnLatency = (() => {
            // Chercher la latence dans le cache pour ce flux
            const cached = Array.from(cdnLatencyCache.entries());
            if (cached.length === 0) return 0;
            // Prendre la latence la plus récente (dernier host CDN connu)
            return cached[cached.length - 1][1];
          })();
          // Si latence CDN > 5s, on augmente le seuil proportionnellement
          // Ex: latence 7s → seuil = max(5, ceil(7000/10000 * 8)) = max(5, 6) = 6
          const dynamicThreshold = cdnLatency > 0
            ? Math.max(NON_FATAL_THRESHOLD_MIN, Math.ceil(cdnLatency / 10_000 * 8))
            : NON_FATAL_THRESHOLD_MIN;

          consecutiveNetErrors++;
          this._log('WDG', sourceId,
            `erreur non-fatale ${consecutiveNetErrors}/${dynamicThreshold}` +
            ` (latence CDN=${cdnLatency}ms)` +
            // Sans le détail ni l'URL, ces lignes ne disent rien de ce qui a échoué :
            // un segment bloqué avant émission et un CDN qui ne répond pas s'y
            // ressemblent trait pour trait.
            ` details=${data.details}` +
            ` url=${data.url ?? data.context?.url ?? data.frag?.url ?? '(inconnue)'}` +
            ` httpCode=${data.networkDetails?.code ?? data.networkDetails?.status ?? '?'}`
          );
          if (consecutiveNetErrors >= dynamicThreshold) {
            console.warn(
              `[HlsAcquirer v4.18] ${consecutiveNetErrors} erreurs réseau non-fatales` +
              ` (seuil adaptatif=${dynamicThreshold}, latence CDN=${cdnLatency}ms)` +
              ` — renouvellement préventif pour ${sourceId}`
            );
            consecutiveNetErrors = 0;
            entry.isRetrying = true;
            this._renewXtream(source, entry).catch(err => {
              console.error(`[HlsAcquirer v4.18] Renouvellement préventif échoué pour ${source.id} :`, err);
            });
          }
        } else {
          if (data.type !== 'networkError') consecutiveNetErrors = 0;
        }
        return;
      }

      // ── Erreur fatale ─────────────────────────────────────────────────────

      if (this._disabledIds.has(sourceId)) {
        console.log(`[HlsAcquirer v4.18] Erreur fatale ignorée — ${sourceId} désactivé`);
        return;
      }

      if (entry.isRetrying) {
        console.warn(`[HlsAcquirer v4.18] Erreur fatale ignorée (retry en cours) pour ${sourceId}`);
        return;
      }

      // v4.19 — grace period post-attach : traitement ALLÉGÉ des erreurs des
      // premières secondes (hls.js émet des levelLoadTimeOut transitoires juste
      // après MANIFEST_PARSED, le temps que la source réponde), mais jamais
      // silencieux — cf. GRACE_RESUME_MS/GRACE_MAX_RESUMES, qui racontent le
      // retour de régie perdu pour de bon le 03/09/2026.
      const msSinceAttach = entry.lastAttachTs ? Date.now() - entry.lastAttachTs : Infinity;
      const graceResumes  = entry.graceResumes ?? 0;
      if (msSinceAttach < WATCHDOG_GRACE_MS && graceResumes < GRACE_MAX_RESUMES) {
        // Ces deux types-là se rattrapent sur place, c'est le contrat de hls.js.
        // Tout le reste (mux, autre) est structurel : la reprise n'y peut rien,
        // et le laisser passer ici le rendrait invisible pour de bon.
        const reprise =
            data.type === 'networkError' ? 'startLoad'
          : data.type === 'mediaError'   ? 'recoverMediaError'
          : null;

        if (reprise) {
          entry.graceResumes = graceResumes + 1;
          console.warn(
            `[HlsAcquirer v4.19] Erreur fatale pendant la grace period` +
            ` (${Math.round(msSinceAttach)}ms < ${WATCHDOG_GRACE_MS}ms) pour ${sourceId}` +
            ` type=${data.type} details=${data.details}` +
            ` — reprise ${reprise} dans ${GRACE_RESUME_MS}ms` +
            ` (${entry.graceResumes}/${GRACE_MAX_RESUMES})`
          );

          // isRetrying pendant l'attente : le verrou est testé plus haut, il
          // empêche une rafale d'erreurs fatales d'empiler autant de reprises.
          entry.isRetrying = true;
          setTimeout(() => {
            entry.isRetrying = false;
            if (!this.entries.has(sourceId))     return;
            if (this._disabledIds.has(sourceId)) return;
            if (!entry.hls)                      return;
            try {
              if (reprise === 'startLoad') entry.hls.startLoad();
              else                         entry.hls.recoverMediaError();
              // Le lecteur a pu se mettre en pause en même temps qu'il perdait
              // son flux : relancer le chargement sans relancer la lecture
              // laisserait un élément muet devant un tampon qui se remplit.
              entry.video.play().catch(() => {});
            } catch (err) {
              console.error(
                `[HlsAcquirer v4.19] Reprise ${reprise} impossible pour ${sourceId} :`, err
              );
            }
          }, GRACE_RESUME_MS);
          return;
        }

        console.warn(
          `[HlsAcquirer v4.19] Erreur fatale pendant la grace period pour ${sourceId}` +
          ` type=${data.type} details=${data.details}` +
          ` — non rattrapable sur place, traitement fatal normal`
        );
      } else if (msSinceAttach < WATCHDOG_GRACE_MS) {
        console.warn(
          `[HlsAcquirer v4.19] Grace period épuisée pour ${sourceId}` +
          ` (${graceResumes} reprise(s) sans succès) — traitement fatal normal`
        );
      }

      const urlTentee      = data.url ?? data.context?.url ?? data.frag?.url ?? '(inconnue)';
      const networkDetails = data.networkDetails;
      const httpCode       = networkDetails?.code ?? networkDetails?.status ?? '?';
      console.error(
        `[HlsAcquirer v4.18] Erreur fatale HLS sur ${source.id}` +
        ` type=${data.type} details=${data.details}` +
        ` url=${urlTentee} httpCode=${httpCode}`
      );

      if (config.xtream) {
        if (data.type === 'mediaError' && mediaErrorAttempts < MAX_MEDIA_ERRORS) {
          mediaErrorAttempts++;
          console.warn(
            `[HlsAcquirer v4.18] mediaError sur ${source.id}` +
            ` — recoverMediaError (tentative ${mediaErrorAttempts}/${MAX_MEDIA_ERRORS})`
          );
          try { entry.hls.recoverMediaError(); } catch {}
          return;
        }

        if (data.type === 'mediaError') {
          console.warn(
            `[HlsAcquirer v4.18] mediaError épuisé (${MAX_MEDIA_ERRORS}) sur ${source.id}` +
            ` — bascule sur _renewXtream`
          );
        }

        mediaErrorAttempts = 0;
        entry.isRetrying   = true;
        this._renewXtream(source, entry).catch(err => {
          console.error(`[HlsAcquirer v4.18] Récupération impossible pour ${source.id} :`, err);
        });

      } else if (config.autoRetry) {
        const delay = this._retryDelay(entry);
        entry.reattachAttempts++;
        entry.isRetrying = true;
        console.log(
          `[HlsAcquirer v4.18] autoRetry — reload manifest dans ${delay}ms` +
          ` (tentative ${entry.reattachAttempts}) pour ${sourceId}`
        );
        setTimeout(async () => {
          if (!this.entries.has(sourceId))     return;
          if (this._disabledIds.has(sourceId)) { entry.isRetrying = false; return; }
          try {
            // L'élément reste muet pendant tout le rechargement : il n'était démuté
            // ici que pour la capture finale, mais l'attente de `canplay` ci-dessous
            // dure jusqu'à 5 s — soit autant de son envoyé aux haut-parleurs à
            // chaque reprise automatique du flux.
            entry.hls?.stopLoad();
            entry.hls?.loadSource(config.url);
            entry.hls?.startLoad(-1);
            await entry.video.play().catch(() => {});

            await new Promise<void>(resolve => {
              if (entry.video.readyState >= 2) { resolve(); return; }
              entry.video.addEventListener('canplay', () => resolve(), { once: true });
              setTimeout(resolve, 5_000);
            });

            const newStream = await this._captureWithAudio(sourceId, entry.video);

            if (newStream) {
              const existingStream = this.registry.getStream(sourceId);
              if (existingStream) {
                for (const oldTrack of existingStream.getTracks()) {
                  existingStream.removeTrack(oldTrack);
                  oldTrack.stop();
                }
                for (const newTrack of newStream.getTracks()) {
                  existingStream.addTrack(newTrack);
                }
                this._rebindAudio(sourceId, existingStream);
              }
            }

            // Filet : _captureWithAudio restaure déjà l'état précédent, on réaffirme
            // l'invariant « l'élément de l'acquirer est muet hors capture ».
            entry.video.muted    = !capturesViaWebAudio(entry.video);
            entry.reattachAttempts = 0;
            console.log(`[HlsAcquirer v4.18] autoRetry — reload OK pour ${sourceId}`);
          } catch (err) {
            console.error(`[HlsAcquirer v4.18] autoRetry reload échoué pour ${sourceId} :`, err);
          } finally {
            entry.isRetrying = false;
          }
        }, delay);

      } else {
        const delay = this._retryDelay(entry);
        entry.reattachAttempts++;
        entry.isRetrying = true;
        console.log(`[HlsAcquirer v4.18] Re-attach HLS dans ${delay}ms (tentative ${entry.reattachAttempts})`);
        setTimeout(async () => {
          if (!this.entries.has(source.id))     return;
          if (this._disabledIds.has(source.id)) { entry.isRetrying = false; return; }
          try {
            await this._attachHls(source.id, config.url, entry, true);
            await entry.video.play().catch(() => {});
            entry.reattachAttempts = 0;
          } catch (err) {
            console.error(`[HlsAcquirer v4.18] Re-attach échoué pour ${source.id} :`, err);
          } finally {
            entry.isRetrying = false;
          }
        }, delay);
      }
    };

    entry.watchdogHandler = watchdog;
    entry.hls.on(HlsLib.Events.ERROR, watchdog);
  }

  // ── _attachHls ─────────────────────────────────────────────────────────────

  private async _attachHls(
    sourceId     : string,
    url          : string,
    entry        : HlsEntry,
    forceRecreate: boolean = true,
  ): Promise<void> {

    console.log(`[HlsAcquirer v4.18] _attachHls ${sourceId} url=${url} forceRecreate=${forceRecreate}`);

    let HlsLib = (window as any).Hls;
    if (!HlsLib) {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.13/hls.min.js');
      HlsLib = (window as any).Hls;
    }

    if (!HlsLib?.isSupported()) {
      if (entry.video.canPlayType('application/vnd.apple.mpegurl')) {
        entry.video.src = url;
        return;
      }
      this.releaseEntry(sourceId);
      throw new Error('HLS non supporté sur ce navigateur');
    }

    // v4.18 — withCredentials uniquement pour les flux directs (MediaMTX).
    const isProxyUrl = url.startsWith(PROXY_PREFIX);

    // v4.18 — resolveBaseUrl lancé en PARALLÈLE de la création hls.js.
    // En séquentiel, resolveBaseUrl pouvait consommer jusqu'à 8-9s sur 3 tentatives,
    // laissant moins d'1s au timeout de 10s de la Promise MANIFEST_PARSED → échec systématique.
    // On lance la résolution immédiatement, puis on attend son résultat juste avant loadSource.
    const baseUrlPromise = isProxyUrl ? resolveBaseUrl(url) : Promise.resolve(null);

    const canReuse = !forceRecreate
      && !!entry.hls
      && !isProxyUrl
      && true; // proxyLoaderConfig sera vide si pas proxy

    if (canReuse) {
      console.log(`[HlsAcquirer v4.18] loadSource rapide (sans destroy) pour ${sourceId}`);
      this._removeWatchdog(entry, HlsLib);
      entry.hls.stopLoad();
      entry.hls.loadSource(url);
      entry.hls.startLoad();

      await new Promise<void>((resolve, reject) => {
        // Le timer est désarmé dès que la promesse est tranchée : autrement il
        // survit à l'échec et vient journaliser un TIMEOUT sur un attachement
        // déjà abandonné — d'où les rafales de « TIMEOUT 15s » sans rapport avec
        // ce qui se joue au moment où elles s'affichent.
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const onError = (_: any, data: any) => {
          if (data.fatal) {
            clearTimeout(timeoutId);
            entry.hls.off(HlsLib.Events.MANIFEST_PARSED, onManifest);
            entry.hls.off(HlsLib.Events.ERROR, onError);
            reject(new Error(`HLS fatal: ${data.type}`));
          }
        };
        const onManifest = () => {
          clearTimeout(timeoutId);
          this._log('ATH', sourceId, `MANIFEST_PARSED (reuse), levels=${entry.hls.levels?.length}`);
          entry.hls.off(HlsLib.Events.ERROR, onError);
          resolve();
        };
        entry.hls.once(HlsLib.Events.MANIFEST_PARSED, onManifest);
        entry.hls.on(HlsLib.Events.ERROR, onError);
        timeoutId = setTimeout(() => {
          entry.hls.off(HlsLib.Events.MANIFEST_PARSED, onManifest);
          entry.hls.off(HlsLib.Events.ERROR, onError);
          reject(new Error('HLS timeout (10s)'));
        }, 10_000);
      });

      this._buildAndRegisterWatchdog(sourceId, entry, HlsLib);
      return;
    }

    // v4.18 — attendre la résolution baseUrl (lancée en parallèle plus haut)
    // À ce stade, hls.js est chargé et l'éventuel ancien hls détruit — la quasi-totalité
    // du budget resolveBaseUrl est déjà écoulée, il ne reste qu'à récupérer le résultat.
    const baseUrl = await baseUrlPromise;
    let proxyLoaderConfig: Record<string, any> = {};
    if (isProxyUrl) {
      if (baseUrl) {
        proxyLoaderConfig = buildProxyLoaderConfig(HlsLib, baseUrl);
        console.log('[HlsAcquirer v4.18] loader custom injecté, baseUrl:', baseUrl);
      } else {
        console.warn('[HlsAcquirer v4.18] baseUrl non résolue, hls.js sans loader custom');
      }
    }

    const hlsConfig: Record<string, any> = {
      ...HLS_BASE_CONFIG,
      ...proxyLoaderConfig,
      ...(isProxyUrl ? {} : buildDirectLoaderConfig(HlsLib, url, sourceId)),
      ...(isProxyUrl || !needsCookies(entry.source.config as HlsSourceConfig, url) ? {} : {
        xhrSetup: (xhr: XMLHttpRequest) => {
          xhr.withCredentials = true;
        },
      }),
    };

    this._destroyHls(entry, HlsLib);

    const hls = new HlsLib(hlsConfig);
    entry.hls = hls;
    hlsInstances.add(new WeakRef(hls));

    // Comptage des octets entrants. hls.js est le seul à les connaître : l'API
    // Resource Timing du navigateur rapporte `transferSize = 0` sur une origine
    // tierce sans `Timing-Allow-Origin`, et tous nos flux viennent d'un autre
    // domaine que la régie. `stats.total` couvre l'en-tête et la charge utile du
    // segment, c'est ce qui est réellement passé sur le lien.
    // `-tmp` : le renouvellement d'un flux attache d'abord le nouveau lecteur sous
    // un identifiant provisoire avant de l'échanger. Compter dessus créerait un
    // tuyau fantôme à côté du vrai, et le vrai paraîtrait muet.
    const idCompte = sourceId.replace(/-tmp$/, '');
    console.warn(`[DÉBITS] 🔌 compteur d'octets posé sur « ${this.registry.get(idCompte)?.label ?? idCompte} » (${idCompte})`);
    hls.on(HlsLib.Events.FRAG_LOADED, (_evt: any, data: any) => {
      const octets = data?.stats?.total ?? data?.frag?.stats?.total ?? 0;
      if (octets <= 0) return;
      // Le libellé du registre est celui que l'opérateur a donné à la source
      // (« RTMP01 », « Playlist ») : c'est lui qu'il faut lire dans l'onglet
      // Débits, pas une URL de segment longue comme le bras.
      noteHlsBytes(idCompte, octets, this.registry.get(idCompte)?.label);
    });

    // v4.18 fix race condition : enregistrer les listeners AVANT loadSource/attachMedia.
    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const onError = (_: any, data: any) => {
        if (data.fatal) {
          clearTimeout(timeoutId);
          const httpCode = data.networkDetails?.code ?? data.networkDetails?.status ?? '?';
          console.error(
            `[HlsAcquirer v4.18] _attachHls erreur fatale ${sourceId}` +
            ` type=${data.type} details=${data.details} httpCode=${httpCode}`
          );
          hls.off(HlsLib.Events.MANIFEST_PARSED, onManifest);
          hls.off(HlsLib.Events.ERROR, onError);
          reject(new Error(`HLS fatal: ${data.type} (${data.details}) httpCode=${httpCode}`));
        }
      };
      const onManifest = () => {
        clearTimeout(timeoutId);
        this._log('ATH', sourceId,
          `MANIFEST_PARSED — levels=${hls.levels?.length}` +
          ` audioTracks=${hls.audioTracks?.length}` +
          ` videoWidth=${entry.video.videoWidth}`
        );
        hls.off(HlsLib.Events.ERROR, onError);
        resolve();
      };
      hls.once(HlsLib.Events.MANIFEST_PARSED, onManifest);
      hls.on(HlsLib.Events.ERROR, onError);
      timeoutId = setTimeout(() => {
        hls.off(HlsLib.Events.MANIFEST_PARSED, onManifest);
        hls.off(HlsLib.Events.ERROR, onError);
        // Si readyState >= 2 au timeout, le manifest a été parsé mais l'événement
        // a été manqué (race condition hls.js interne) — on résout quand même.
        if (entry.video.readyState >= 2) {
          console.warn(
            `[HlsAcquirer v4.18] _attachHls timeout mais readyState=${entry.video.readyState}` +
            ` pour ${sourceId} — MANIFEST_PARSED manqué, on résout quand même`
          );
          resolve();
        } else {
          console.error(`[HlsAcquirer v4.18] _attachHls TIMEOUT 15s pour ${sourceId}, readyState=${entry.video.readyState}`);
          reject(new Error('HLS timeout (15s)'));
        }
      }, 15_000);

      this._log('ATH', sourceId, `loadSource + attachMedia`);
      hls.loadSource(url);
      hls.attachMedia(entry.video);
    });

    // Nouvel attach : nouvelle période de grâce, et donc nouveau crédit de
    // reprises. Sans cette remise à zéro, le crédit serait consommé une fois
    // pour toutes et un flux qui a hoqueté au démarrage ne serait plus jamais
    // rattrapé au démarrage suivant.
    entry.lastAttachTs = Date.now();
    entry.graceResumes = 0;
    this._buildAndRegisterWatchdog(sourceId, entry, HlsLib);
    this._startFreezeGuard(sourceId, entry);
  }

  // ── _rebindAudio ───────────────────────────────────────────────────────────

  private _rebindAudio(sourceId: string, stream: MediaStream): void {
    if (!this.audioSourceRegistry) {
      console.warn(`[HlsAcquirer v4.18] audioSourceRegistry non injecté — rebind ignoré pour ${sourceId}`);
      return;
    }
    const audioRegId = `audio-${sourceId}`;
    if (!this.audioSourceRegistry.has(audioRegId)) {
      console.warn(`[HlsAcquirer v4.18] rebindAudioTrack ignoré — ${audioRegId} absent du registry`);
      return;
    }
    this.audioSourceRegistry.rebindAudioTrack(audioRegId, stream);
    // Les nœuds de gain sont conservés par rebindAudioTrack — ne pas les écraser
    console.log(`[HlsAcquirer v4.18] rebind audio OK pour ${audioRegId}`);
  }

  // ── _renewXtream ───────────────────────────────────────────────────────────
  // v4.15/v4.18 — appel immédiat, backoff seulement sur échec.
  // isRetrying libéré explicitement dans les deux chemins (succès et échec).

  private async _renewXtream(source: Source, entry: HlsEntry): Promise<void> {
    console.log(`[HlsAcquirer v4.18] _renewXtream START pour ${source.id}`);

    const config = source.config as HlsSourceConfig;
    if (!config.xtream)                   { entry.isRetrying = false; return; }
    if (!this.entries.has(source.id))     { entry.isRetrying = false; return; }
    if (this._disabledIds.has(source.id)) { entry.isRetrying = false; return; }

    let success = false;

    try {
      const newUrl = resolveLiveStreamUrl(config.xtream, config.xtream.streamId);
      config.url = newUrl;
      console.log(`[HlsAcquirer v4.18] _renewXtream nouvelle URL : ${newUrl}`);

      // v4.18 — swap sans coupure : préparer le nouveau stream dans une <video>
      // temporaire pendant que l'ancienne continue de jouer.
      // Le swap n'est effectué qu'une fois le nouveau stream opérationnel.
      const tmpVideo = Object.assign(document.createElement('video'), {
        muted: true, autoplay: true, playsInline: true, crossOrigin: 'anonymous',
      });
      tmpVideo.style.display = 'none';
      document.body.appendChild(tmpVideo);

      const tmpEntry: HlsEntry = {
        video           : tmpVideo,
        source          : entry.source,
        reattachAttempts: 0,
        isRetrying      : false,
        watchdogHandler : null,
      };

      try {
        await this._attachHls(source.id + '-tmp', newUrl, tmpEntry, true);
      } catch (attachErr) {
        // Neutraliser le watchdog et détruire proprement avant de propager
        const HlsLibTmp = (window as any).Hls;
        if (HlsLibTmp && tmpEntry.hls && tmpEntry.watchdogHandler) {
          try { tmpEntry.hls.off(HlsLibTmp.Events.ERROR, tmpEntry.watchdogHandler); } catch {}
          tmpEntry.watchdogHandler = null;
        }
        try { tmpEntry.hls?.destroy(); } catch {}
        tmpVideo.remove();
        throw attachErr;
      }

      await Promise.race([
        tmpVideo.play(),
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error('play() timeout 3s')), 3_000)
        ),
      ]).catch(e => {
        console.warn(`[HlsAcquirer v4.18] play() tmpVideo non bloquant :`, e);
      });

      await waitForVideoData(tmpVideo, 10_000);

      // Même contrainte que dans acquire() : capturer un élément resté muet donne
      // une piste audio silencieuse. Ce chemin (renouvellement Xtream) capturait
      // tmpVideo sans jamais le démuter — la source repartait donc muette après
      // renouvellement, sans que rien ne le signale.
      const newStream = await this._captureWithAudio(source.id, tmpVideo);

      const tracks = newStream?.getTracks() ?? [];
      console.log(
        `[HlsAcquirer v4.18] captureStream pour ${source.id}` +
        ` tracks=${tracks.map((t: MediaStreamTrack) => `${t.kind}:${t.readyState}`).join(',') || 'vide'}`
      );

      if (newStream && tracks.length > 0) {
        const HlsLib = (window as any).Hls;

        // Neutraliser le watchdog de tmpEntry AVANT le swap pour éviter qu'il
        // déclenche _renewXtream sur l'id principal après la bascule.
        if (HlsLib && tmpEntry.hls && tmpEntry.watchdogHandler) {
          try { tmpEntry.hls.off(HlsLib.Events.ERROR, tmpEntry.watchdogHandler); } catch {}
          tmpEntry.watchdogHandler = null;
        }

        // Swap atomique : l'ancien hls.js est détruit seulement maintenant
        if (HlsLib && entry.hls) {
          this._destroyHls(entry, HlsLib);
        }
        entry.video.pause();
        entry.video.srcObject = null;
        entry.video.src = '';
        stopMediaElementCapture(entry.video);
        entry.video.remove();

        // Basculer l'entry sur la nouvelle video/hls
        entry.video        = tmpVideo;
        entry.hls          = tmpEntry.hls;
        entry.lastAttachTs = tmpEntry.lastAttachTs;
        // Le crédit de reprises suit l'attach dont il décrit la grâce : c'est
        // celui du lecteur provisoire qu'on adopte, pas celui qu'on abandonne.
        entry.graceResumes = tmpEntry.graceResumes ?? 0;

        // Ré-enregistrer le watchdog sur la nouvelle instance hls avec l'id correct
        if (HlsLib && entry.hls) {
          this._buildAndRegisterWatchdog(source.id, entry, HlsLib);
        }

        const existingStream = this.registry.getStream(source.id);
        if (existingStream) {
          for (const oldTrack of existingStream.getTracks()) {
            existingStream.removeTrack(oldTrack);
            oldTrack.stop();
          }
          for (const newTrack of newStream.getTracks()) {
            existingStream.addTrack(newTrack);
          }
          console.log(`[HlsAcquirer v4.18] swap sans coupure OK pour ${source.id}`);
          // Rebrancher les listeners de buffering sur la nouvelle video
          this._attachBufferingListeners(source.id, tmpVideo);
          this._rebindAudio(source.id, existingStream);
        } else {
          this.registry.injectStream(source.id, newStream);
          console.log(`[HlsAcquirer v4.18] injectStream fallback pour ${source.id}`);
        }
      } else {
        try { tmpEntry.hls?.destroy(); } catch {}
        stopMediaElementCapture(tmpVideo);
        tmpVideo.remove();
        console.warn(
          `[HlsAcquirer v4.18] stream vide pour ${source.id}` +
          ` (readyState=${tmpVideo.readyState}) — le watchdog relancera`
        );
      }

      entry.reattachAttempts = 0;
      success = true;
      console.log(`[HlsAcquirer v4.18] URL Xtream renouvelée pour ${source.id}`);

    } catch (err) {
      console.error(`[HlsAcquirer v4.18] Échec renouvellement ${source.id} :`, err);

      if (err instanceof Error && /401|403|unauthorized|forbidden/i.test(err.message)) {
        console.error(
          `[HlsAcquirer v4.18] Erreur auth Xtream pour ${source.id} — vérifiez config.xtream.`
        );
      }

      entry.reattachAttempts++;
      const delay = this._retryDelay(entry);
      console.log(`[HlsAcquirer v4.18] Prochain essai dans ${delay}ms pour ${source.id}`);

      // Libère isRetrying après le délai pour permettre au watchdog de
      // re-déclencher si une nouvelle erreur fatale arrive entre-temps.
      setTimeout(() => {
        if (!this.entries.has(source.id))     return;
        if (this._disabledIds.has(source.id)) return;
        entry.isRetrying = false;
      }, delay);

    } finally {
      // Sur succès : libère immédiatement.
      // Sur échec  : le setTimeout ci-dessus gère la libération différée —
      //              on ne libère PAS ici pour garder le verrou pendant le délai.
      if (success) {
        entry.isRetrying = false;
      }
    }
  }

  // ── _waitCanPlay ───────────────────────────────────────────────────────────

  private _waitCanPlay(sourceId: string, video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      if (video.readyState >= 2) {
        this._log('WCP', sourceId, `readyState déjà OK (${video.readyState})`);
        resolve();
        return;
      }
      this._log('WCP', sourceId, `attente canplay, readyState actuel=${video.readyState}`);

      const onCanPlay = () => {
        this._log('WCP', sourceId, `canplay reçu, readyState=${video.readyState} videoWidth=${video.videoWidth}`);
        cleanup();
        resolve();
      };
      const onError = () => {
        this._log('WCP', sourceId, `video error event`);
        cleanup();
        this.releaseEntry(sourceId);
        reject(new Error('Impossible de lire la source'));
      };
      const cleanup = () => {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('error',   onError);
      };
      video.addEventListener('canplay', onCanPlay, { once: true });
      video.addEventListener('error',   onError,   { once: true });
      setTimeout(() => {
        cleanup();
        console.error(
          `[HlsAcquirer v4.18] _waitCanPlay TIMEOUT 15s pour ${sourceId}` +
          ` readyState=${video.readyState} videoWidth=${video.videoWidth} paused=${video.paused}`
        );
        this.releaseEntry(sourceId);
        reject(new Error('Timeout lecture (15s)'));
      }, 15_000);
    });
  }

  // ── releaseEntry ───────────────────────────────────────────────────────────

  releaseEntry(sourceId: string): void {
    this._stopFreezeGuard(sourceId);
    this._detachBufferingListeners(sourceId);
    // Le tuyau se referme : son compteur de débit n'a plus lieu d'être affiché.
    forgetHlsSource(sourceId);
    const entry = this.entries.get(sourceId);
    if (!entry) return;

    entry.loopAbort?.abort();
    entry.loopAbort = undefined;

    try {
      const HlsLib = (window as any).Hls;
      if (HlsLib && entry.hls) {
        this._destroyHls(entry, HlsLib);
      } else {
        entry.hls?.destroy();
        entry.hls = undefined;
      }
      entry.dash?.reset();
      entry.video.pause();
      entry.video.srcObject = null;
      entry.video.src       = '';
      entry.video.load();
      stopMediaElementCapture(entry.video);
      entry.video.remove();
    } catch {}

    this.entries.delete(sourceId);
  }
}